import type { ReactNode } from 'react'
import { useSelector } from 'react-redux'

import { MANAGER_COCKPIT_TOUR_TARGETS as TOUR_TARGETS } from 'partner360/configs/widgetTour/managerCockpitTourTargets'

import { getCockpitPlanningStatus, useManagerCockpitPlanningStatuses } from '../../model/hooks'
import { selectCockpitOffering } from '../../model/selectors'
import { CockpitHelpTools } from '../cockpitHelpPanel'
import type { ICockpitHelpTour } from '../cockpitHelpPanel'
import { ManagerCockpitProgramTypes } from '../managerCockpitProgramTypes/ManagerCockpitProgramTypes'

import { CockpitBoardSlide } from './components/cockpitBoardSlide'
import { CockpitPlanDeck } from './components/cockpitPlanDeck'
import { CockpitSectionDeck } from './components/cockpitSectionDeck'
import { CockpitSidebarToggle } from './components/cockpitSidebarToggle'
import type { ICockpitSidebarToggle } from './components/cockpitSidebarToggle'

import styles from './CockpitScopePanel.module.scss'

interface IManagerCockpitScopePanelProps {
  tour?: ICockpitHelpTour
  /** Leads the deck with the button folding the page's filter column. */
  sidebarToggle?: ICockpitSidebarToggle
  /** The board the tabs switch. */
  children: ReactNode
  /** A tile of the board is expanded, so the deck folds to give it the height. */
  isTileExpanded?: boolean
}

/** The manager cockpit's tray, drawn as the single-partner one (`CockpitScopePanel`): the plan
 *  deck holds the year, the programme tabs and the selected programme's state across the partners
 *  in view, the selected programme's board sliding in under it. The board fills the tray, which
 *  takes the height the screen leaves it. */
export const ManagerCockpitScopePanel = ({
  tour,
  sidebarToggle,
  children,
  isTileExpanded = false,
}: IManagerCockpitScopePanelProps) => {
  const selectedOffering = useSelector(selectCockpitOffering)
  const statuses = useManagerCockpitPlanningStatuses()

  const deck = {
    status: getCockpitPlanningStatus(statuses, selectedOffering),
    offerings: <ManagerCockpitProgramTypes embedded />,
    tools: (
      <CockpitHelpTools mode="manager" tour={tour} tourId={TOUR_TARGETS.trainingCentre} compact />
    ),
    tourTargets: TOUR_TARGETS,
  }

  return (
    <div className={styles.tray}>
      <CockpitPlanDeck
        {...deck}
        lead={sidebarToggle && <CockpitSidebarToggle {...sidebarToggle} appearance="chip" />}
        folded={isTileExpanded}
      />
      {/* The same deck over a tile opened over the whole screen, whose scrim covers this one. The
          filter column it would fold stands under the scrim, so it comes without that button. */}
      <CockpitSectionDeck deck={deck} tabs={<ManagerCockpitProgramTypes embedded />} />
      <CockpitBoardSlide tileTargets={TOUR_TARGETS} fill>
        {children}
      </CockpitBoardSlide>
    </div>
  )
}

ManagerCockpitScopePanel.displayName = 'ManagerCockpitScopePanel'
